import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MessageCircle } from "lucide-react";
import {
  type Identifier,
  useAuthProvider,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetMany,
  useGetOne,
  useNotify,
  useTranslate,
} from "ra-core";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { MessagePlaceholderValues } from "../activityLog/activityLogRules";
import { PickerButton } from "../activityLog/PickerButton";
import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import { buildWhatsAppLink } from "../dashboard/hoyPlan";
import { findDealLabel } from "../deals/dealUtils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type {
  Deal,
  DealActivityResult,
  DealActivityType,
  DealStageActivity,
  Project,
  Task,
} from "../types";
import {
  findWhatsAppActivity,
  findWhatsAppResult,
  formatWhatsAppLogText,
  resolveGenericWhatsAppMessage,
  resolveLeadWhatsAppMessage,
} from "./whatsappMessage";

/**
 * Almikia Epic EW1 (TASK-124): the shared "Enviar WhatsApp" action -- one
 * reusable button (prefilled link + message precedence + automatic logging
 * + lead choice) that TASK-125 (Hoy), TASK-126 (lead show) and TASK-127
 * (contact show) place, so this behavior never gets duplicated. This ticket
 * places the action nowhere; e2e coverage lands with wave 3.
 */

const CATALOGUE_PAGINATION = { page: 1, perPage: 1000 } as const;
const CATALOGUE_SORT = { field: "position", order: "ASC" as const };

export interface WhatsAppActionContact {
  id: Identifier;
  first_name: string;
  phone_jsonb: readonly { number: string }[] | null;
}

export interface WhatsAppActionProps {
  contact: WhatsAppActionContact;
  /**
   * A lead already on screen (lead show, or a Hoy card already tied to one
   * lead). Omit it to have the action look up the contact's own writable
   * leads (ticket rule 5).
   */
  dealId?: Identifier | null;
  className?: string;
  /** Already-reviewed text to prefill instead of the catalogue message (AI copilot, Epic EI). */
  messageOverride?: string;
  /** Renders a text button with this label instead of the icon button. */
  label?: string;
  /** Called when the person taps the link (after WhatsApp opens). */
  onSent?: () => void;
}

type WhatsAppTarget =
  | { kind: "loading" }
  | { kind: "known"; dealId: Identifier }
  | { kind: "none" }
  | { kind: "choose"; deals: Deal[] };

const ICON_BUTTON_CLASSES =
  "flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F5EC] text-[#23794A] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-95";

const TEXT_BUTTON_CLASSES =
  "inline-flex h-11 items-center justify-center rounded-[12px] bg-[image:var(--gradient-brand-button)] px-4 text-sm font-bold text-white shadow-xs hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98]";

function firstPhoneNumber(contact: WhatsAppActionContact): string | null {
  return contact.phone_jsonb?.[0]?.number ?? null;
}

/**
 * Almikia Epic EW1 (TASK-124): the shared action itself -- resolves the
 * prefilled message from already-cached catalogue data (NFR: no network
 * wait before the tap), opens WhatsApp, and logs the guarded `deal_notes`
 * activity entry in the background when the user can write the lead.
 */
export function WhatsAppAction({
  contact,
  dealId = null,
  className,
  messageOverride,
  label,
  onSent,
}: WhatsAppActionProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const authProvider = useAuthProvider();
  const queryClient = useQueryClient();
  const { identity, isPending: identityPending } = useGetIdentity();
  const { dealStages } = useConfigurationContext();
  const [chooserOpen, setChooserOpen] = useState(false);

  const phone = firstPhoneNumber(contact);

  // The lead-catalogue queries (Almikia Epic EA): identical resource/params
  // to `LogActivityForm.tsx`'s own fetch, so react-query's cache is shared
  // across every `WhatsAppAction` instance and any open `LogActivityForm`
  // -- placing this action in a list of N cards adds no per-card request
  // (NFR).
  const { data: activityTypes = [], isPending: activityTypesPending } =
    useGetList<DealActivityType>("deal_activity_types", {
      filter: {},
      pagination: CATALOGUE_PAGINATION,
      sort: CATALOGUE_SORT,
    });
  const { data: activityResults = [], isPending: activityResultsPending } =
    useGetList<DealActivityResult>("deal_activity_results", {
      filter: {},
      pagination: CATALOGUE_PAGINATION,
      sort: CATALOGUE_SORT,
    });
  const { data: stageActivities = [], isPending: stageActivitiesPending } =
    useGetList<DealStageActivity>("deal_stage_activities", {
      filter: {},
      pagination: CATALOGUE_PAGINATION,
      sort: { field: "id", order: "ASC" },
    });

  const whatsappActivity = useMemo(
    () => findWhatsAppActivity(activityTypes),
    [activityTypes],
  );
  const whatsappResult = useMemo(
    () => findWhatsAppResult(activityResults, whatsappActivity?.id),
    [activityResults, whatsappActivity],
  );

  const values: MessagePlaceholderValues = useMemo(
    () => ({
      nombre: contact.first_name,
      asesora: identity?.fullName?.split(" ")[0] ?? null,
    }),
    [contact.first_name, identity?.fullName],
  );

  // Ticket rule 5: only look up the contact's own leads when the caller
  // didn't already give a known lead.
  const contactOnly = dealId == null;
  const {
    data: candidateLeads = [],
    isPending: candidatesPending,
    error: candidatesError,
    refetch: refetchCandidates,
  } = useGetList<Deal>(
    "deals",
    {
      filter: { "contact_ids@cs": `{${contact.id}}`, "archived_at@is": null },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "created_at", order: "DESC" },
    },
    // `retry: false`: the chooser already ships a designed error state with
    // its own manual "Retry" button (rule 12) -- a silent background retry
    // would just delay that state showing up on a real failure.
    { enabled: contactOnly, retry: false },
  );

  // Ticket rule 5's writability filter -- exactly the lead show's own
  // `deal_notes` create gate, checked per candidate (a handful of leads at
  // most, never one request per card).
  const [writableLeads, setWritableLeads] = useState<Deal[] | null>(null);
  useEffect(() => {
    if (!contactOnly || candidatesPending || candidatesError) {
      setWritableLeads(null);
      return;
    }
    let cancelled = false;
    Promise.all(
      candidateLeads.map(async (lead) => {
        const allowed =
          (await authProvider?.canAccess?.({
            action: "create",
            resource: "deal_notes",
            record: lead,
          })) ?? false;
        return allowed ? lead : null;
      }),
    ).then((results) => {
      if (!cancelled) {
        setWritableLeads(results.filter((lead): lead is Deal => lead != null));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    contactOnly,
    candidateLeads,
    candidatesPending,
    candidatesError,
    authProvider,
  ]);

  const target: WhatsAppTarget = useMemo(() => {
    if (!contactOnly) return { kind: "known", dealId: dealId as Identifier };
    if (candidatesPending) return { kind: "loading" };
    if (candidatesError) return { kind: "choose", deals: [] };
    if (writableLeads == null) return { kind: "loading" };
    if (writableLeads.length === 0) return { kind: "none" };
    if (writableLeads.length === 1) {
      return { kind: "known", dealId: writableLeads[0].id };
    }
    return { kind: "choose", deals: writableLeads };
  }, [contactOnly, dealId, candidatesPending, candidatesError, writableLeads]);

  const activeDealId = target.kind === "known" ? target.dealId : undefined;
  const { data: deal, isPending: dealPending } = useGetOne<Deal>(
    "deals",
    { id: activeDealId as Identifier },
    { enabled: activeDealId != null },
  );
  const { data: openTasks = [], isPending: openTasksPending } =
    useGetList<Task>(
      "tasks",
      {
        filter: {
          deal_id: activeDealId,
          "done_date@is": null,
          "activity_type_id@not.is": null,
        },
        pagination: { page: 1, perPage: 5 },
        sort: { field: "id", order: "ASC" },
      },
      { enabled: activeDealId != null },
    );
  const { data: project, isPending: projectPending } = useGetOne<Project>(
    "projects",
    { id: deal?.project_id as Identifier },
    { enabled: deal?.project_id != null },
  );

  const leadValues: MessagePlaceholderValues = useMemo(
    () => ({ ...values, proyecto: project?.name }),
    [values, project?.name],
  );

  // Ticket rule 5, several writable leads: one batched request (never one
  // per candidate) covering every candidate's own open next-step task, so
  // picking one from the chooser still opens WhatsApp with THAT lead's
  // message (rule 2's full precedence), ready before the tap.
  const chooserLeads = target.kind === "choose" ? target.deals : [];
  const chooserDealIds = chooserLeads.map((lead) => lead.id);
  const {
    data: openTasksForCandidates = [],
    isPending: openTasksForCandidatesPending,
  } = useGetList<Task>(
    "tasks",
    {
      filter: {
        "deal_id@in": `(${chooserDealIds.join(",")})`,
        "done_date@is": null,
        "activity_type_id@not.is": null,
      },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: chooserDealIds.length > 0 },
  );

  // Ticket rules 2/5: a candidate's own {proyecto} (precedence step (a)/(b)
  // templates can reference it) needs its project too -- one batched
  // `useGetMany` covering every candidate that has one, never one per lead.
  const chooserProjectIds = [
    ...new Set(
      chooserLeads
        .map((lead) => lead.project_id)
        .filter((id): id is Identifier => id != null),
    ),
  ];
  const { data: chooserProjects = [], isPending: chooserProjectsPending } =
    useGetMany<Project>(
      "projects",
      { ids: chooserProjectIds },
      { enabled: chooserProjectIds.length > 0 },
    );
  const chooserProjectsById = useMemo(
    () =>
      new Map(chooserProjects.map((candidate) => [candidate.id, candidate])),
    [chooserProjects],
  );

  // Ticket rule 2/NFR: the tap must never carry a lower-priority/fallback
  // message just because a dependency was still in flight -- every input
  // the precedence chain reads (catalogue, identity, the lead's own open
  // task, stage activities, its project) must have settled first.
  const catalogueReady =
    !activityTypesPending && !activityResultsPending && !identityPending;

  const chooserReady =
    !candidatesPending &&
    writableLeads != null &&
    !openTasksForCandidatesPending &&
    (chooserProjectIds.length === 0 || !chooserProjectsPending) &&
    catalogueReady &&
    !stageActivitiesPending;
  const knownReady =
    target.kind === "known" &&
    catalogueReady &&
    !stageActivitiesPending &&
    !dealPending &&
    deal != null &&
    !openTasksPending &&
    (deal.project_id == null || !projectPending);
  const noneReady = target.kind === "none" && catalogueReady;

  const resolvedMessage =
    knownReady && deal
      ? resolveLeadWhatsAppMessage({
          deal,
          openTasksForDeal: openTasks,
          activityTypes,
          stageActivities,
          whatsappActivity,
          values: leadValues,
        })
      : noneReady
        ? resolveGenericWhatsAppMessage({ whatsappActivity, values })
        : undefined;

  // A reviewed text (AI copilot, Epic EI) replaces the catalogue message
  // once the target lead is known; logging and link building are unchanged.
  const message =
    messageOverride != null && (knownReady || noneReady)
      ? messageOverride
      : resolvedMessage;

  const link =
    phone && message != null ? buildWhatsAppLink(phone, message) : undefined;

  /** Ticket rule 3/4: the guarded `deal_notes` insert, silent when the gate or the catalogue rejects it, a non-blocking notice for any other failure. */
  const logWhatsAppContact = async (targetDeal: Deal) => {
    if (!whatsappActivity || whatsappActivity.archived_at != null) return;
    if (!whatsappResult || whatsappResult.archived_at != null) return;
    if (identity?.id == null) return;
    try {
      const allowed =
        (await authProvider?.canAccess?.({
          action: "create",
          resource: "deal_notes",
          record: targetDeal,
        })) ?? false;
      if (!allowed) return;
      await dataProvider.create("deal_notes", {
        data: {
          deal_id: targetDeal.id,
          text: formatWhatsAppLogText(
            whatsappActivity.name,
            whatsappResult.label,
          ),
          date: new Date().toISOString(),
          sales_id: identity?.id,
          activity_type_id: whatsappActivity.id,
          activity_result_id: whatsappResult.id,
        },
      });
      queryClient.invalidateQueries({ queryKey: ["deal_notes"] });
    } catch {
      notify("crm.whatsapp.not_recorded_notice", {
        type: "warning",
        messageArgs: {
          _: "Se abrió WhatsApp, pero no se pudo registrar el contacto en el lead",
        },
      });
    }
  };

  const accessibleLabel = translate("crm.whatsapp.action_label", {
    nombre: contact.first_name,
    _: `Enviar WhatsApp a ${contact.first_name}`,
  });

  if (!phone) return null;

  // Contact-only, several writable leads (ticket rule 5): the tap opens the
  // chooser instead of WhatsApp directly -- the precise lead is ambiguous.
  if (target.kind === "choose") {
    const openChooser = () => setChooserOpen(true);
    const handlePick = (lead: Deal) => {
      const leadOpenTasks = openTasksForCandidates.filter(
        (task) => task.deal_id === lead.id,
      );
      // Ticket rule 2's {proyecto}: the picked candidate's own project, from
      // the batched `chooserProjectsById` lookup above -- not fetched again
      // here, and not left unfillable (a `prospecto`+project lead must still
      // get its (b) stage-activity template, per acceptance criterion 3).
      const leadValuesForPick: MessagePlaceholderValues = {
        ...values,
        proyecto:
          lead.project_id != null
            ? chooserProjectsById.get(lead.project_id)?.name
            : undefined,
      };
      const leadMessage = resolveLeadWhatsAppMessage({
        deal: lead,
        openTasksForDeal: leadOpenTasks,
        activityTypes,
        stageActivities,
        whatsappActivity,
        values: leadValuesForPick,
      });
      window.open(
        buildWhatsAppLink(phone, leadMessage),
        "_blank",
        "noopener,noreferrer",
      );
      setChooserOpen(false);
      void logWhatsAppContact(lead);
    };
    const handleOpenWithoutLogging = () => {
      const genericMessage = resolveGenericWhatsAppMessage({
        whatsappActivity,
        values,
      });
      window.open(
        buildWhatsAppLink(phone, genericMessage),
        "_blank",
        "noopener,noreferrer",
      );
      setChooserOpen(false);
    };

    return (
      <>
        <button
          type="button"
          onClick={openChooser}
          aria-label={accessibleLabel}
          className={
            className
              ? `${ICON_BUTTON_CLASSES} ${className}`
              : ICON_BUTTON_CLASSES
          }
        >
          <MessageCircle aria-hidden="true" className="h-5 w-5" />
        </button>
        <Sheet open={chooserOpen} onOpenChange={setChooserOpen}>
          <SheetContent side="bottom" aria-describedby={undefined}>
            <SheetHeader>
              <SheetTitle>
                {translate("crm.whatsapp.chooser.title", {
                  _: "¿A qué lead corresponde?",
                })}
              </SheetTitle>
            </SheetHeader>
            <div className="flex flex-col gap-2 p-4">
              {candidatesError ? (
                <ListErrorState
                  message={translate("crm.whatsapp.chooser.error", {
                    _: "No se pudieron cargar tus leads",
                  })}
                  onRetry={() => refetchCandidates()}
                />
              ) : candidatesPending || writableLeads == null ? (
                <ListCardsSkeleton rows={2} />
              ) : (
                target.deals.map((lead) => (
                  <PickerButton
                    key={lead.id}
                    selected={false}
                    disabled={!chooserReady}
                    onClick={() => handlePick(lead)}
                  >
                    {lead.name} · {findDealLabel(dealStages, lead.stage)}
                  </PickerButton>
                ))
              )}
              <PickerButton selected={false} onClick={handleOpenWithoutLogging}>
                {translate("crm.whatsapp.chooser.open_without_logging", {
                  _: "Abrir sin registrar",
                })}
              </PickerButton>
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  if (!link) {
    return (
      <Skeleton
        aria-hidden="true"
        className={
          className
            ? `h-11 w-11 rounded-xl ${className}`
            : "h-11 w-11 rounded-xl"
        }
      />
    );
  }

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label ?? accessibleLabel}
      onClick={() => {
        if (target.kind === "known" && deal) void logWhatsAppContact(deal);
        onSent?.();
      }}
      className={
        label
          ? cn(TEXT_BUTTON_CLASSES, className)
          : className
            ? `${ICON_BUTTON_CLASSES} ${className}`
            : ICON_BUTTON_CLASSES
      }
    >
      {label ?? <MessageCircle aria-hidden="true" className="h-5 w-5" />}
    </a>
  );
}
